/**
 * React hook for managing end-to-end encryption (EC P-384).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useAppSelector } from "./redux";
import { selectRouterE2eeEnabled } from "@/features/auth/authSlice";
import {
  initializeEncryption,
  getResolvedKeyId,
  createPublicKeyShareMessage,
  sendKeySystemCommand,
} from "@/services/keyExchangeService";
import { getPublicKeySetByUsername } from "@/services/keyStorageService";
import { encryptForGroupAddress, encryptForPeer, type PeerCiphertext } from "@/services/outboundEncryption";
import {
  getOrGenerateGroupSymmetricKey,
  distributeGroupKeyToMembers,
  resolveGroupSymmetricKey,
  hasCurrentGroupKey,
} from "@/services/groupKeyService";
import { GroupMember } from "@/types/users";

let keyAutomationUserId: number | null = null;

export type EncryptionFailureReason = "insecure" | "storageBlocked" | "storageFull" | "unknown";

export interface EncryptionStatus {
  isInitialized: boolean;
  isLoading: boolean;
  error: string | null;
  keyId: string | null;
  /** Why init gave up (set with `error`); picks the explanation shown (ENCRYPTION_FAILURE_REASON). */
  failureReason?: EncryptionFailureReason;
}

/** Problems no retry can fix: Web Crypto exists only in secure contexts (HTTPS / localhost), keys live in IDB. */
function environmentFailure(): EncryptionFailureReason | null {
  if (!window.isSecureContext || !globalThis.crypto?.subtle) return "insecure";
  if (typeof indexedDB === "undefined") return "storageBlocked";
  return null;
}

/** Storage blocked = site data disallowed (SecurityError) or IDB unavailable, e.g. older private modes (InvalidStateError). */
function failureReasonOf(error: unknown): EncryptionFailureReason {
  const name = (error as { name?: string } | null)?.name;
  if (name === "QuotaExceededError") return "storageFull";
  if (name === "SecurityError" || name === "InvalidStateError") return "storageBlocked";
  return "unknown";
}

export function useEncryption() {
  const userId = useAppSelector((state) => state.auth.userId);
  const username = useAppSelector((state) => state.auth.username);
  const routerE2eeEnabled = useAppSelector(selectRouterE2eeEnabled);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initAttemptRef = useRef(0);
  const [status, setStatus] = useState<EncryptionStatus>({
    isInitialized: false,
    isLoading: false,
    error: null,
    keyId: null,
  });

  // Initialize encryption when auth context becomes available.
  useEffect(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    const init = async () => {
      console.debug(`[E2EE] init() fired — userId=${userId} username=${username}`);
      if (!routerE2eeEnabled) {
        setStatus({ isInitialized: false, isLoading: false, error: null, keyId: null });
        return;
      }
      if (!userId || !username) {
        console.debug(`[E2EE] init() guard: missing userId or username, resetting status`);
        initAttemptRef.current = 0;
        keyAutomationUserId = null;
        setStatus({ isInitialized: false, isLoading: false, error: null, keyId: null });
        return;
      }

      // Fast path: key already resolved in this JS runtime (re-login without page reload).
      // Skip the isLoading state entirely — go straight to isInitialized.
      const cachedKeyId = getResolvedKeyId(Number(userId));
      if (cachedKeyId) {
        console.debug(`[E2EE] fast-path: reusing cached keyId=${cachedKeyId}`);
        initAttemptRef.current = 0;
        setStatus({ isInitialized: true, isLoading: false, error: null, keyId: cachedKeyId });
        return;
      }

      const envFailure = environmentFailure();
      if (envFailure) {
        console.error(`[E2EE] Encryption unavailable in this browser context: ${envFailure}`);
        setStatus({ isInitialized: false, isLoading: false, error: envFailure, keyId: null, failureReason: envFailure });
        return;
      }

      try {
        setStatus((prev) => ({ ...prev, isLoading: true, error: null }));
        console.debug(`[E2EE] calling initializeEncryption(${Number(userId)})`);
        const keyId = await initializeEncryption(Number(userId));
        console.log(`[E2EE] initializeEncryption resolved: keyId=${keyId} → setting isInitialized=true`);
        initAttemptRef.current = 0;
        setStatus({ isInitialized: true, isLoading: false, error: null, keyId });
      } catch (error) {
        console.error("Failed to initialize encryption:", error);
        const nextAttempt = initAttemptRef.current + 1;
        initAttemptRef.current = nextAttempt;
        const maxAttempts = 4;

        setStatus({
          isInitialized: false,
          isLoading: nextAttempt < maxAttempts,
          error:
            nextAttempt >= maxAttempts
              ? error instanceof Error ? error.message : "Unknown error"
              : null,
          keyId: null,
          failureReason: nextAttempt >= maxAttempts ? failureReasonOf(error) : undefined,
        });

        if (nextAttempt < maxAttempts) {
          retryTimeoutRef.current = setTimeout(init, 1200);
        }
      }
    };

    init();

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, [userId, username, routerE2eeEnabled]);

  // Auto-share public keys with @keys relay when initialized
  useEffect(() => {
    const automateKeyExchange = async () => {
      if (!status.isInitialized || !status.keyId || !userId || !username) return;

      const uid = Number(userId);
      if (keyAutomationUserId !== uid) keyAutomationUserId = uid;

      try {
        // v2: keys are now shared as SPKI (X509) for Android compatibility.
        // The version suffix forces a one-time re-share so any stale JWK in @keys
        // is replaced with SPKI. Remove the old v1 key to keep sessionStorage tidy.
        const cacheKey = `e2ee:autoShare:v2:lastSharedKeyId:${uid}`;
        sessionStorage.removeItem(`e2ee:autoShare:lastSharedKeyId:${uid}`);
        const lastShared = sessionStorage.getItem(cacheKey);

        if (lastShared !== status.keyId) {
          const cmd = await createPublicKeyShareMessage(uid);
          console.log(`[E2EE] Posting sendkeys: uid=${uid} keyId=${status.keyId}`);
          await sendKeySystemCommand(cmd, uid);
          sessionStorage.setItem(cacheKey, status.keyId);
        } else if (import.meta.env.DEV) {
          console.debug(`[E2EE] Skip auto-share: already shared keyId=${status.keyId}`);
        }
      } catch (error) {
        console.error("Failed to auto-share key (sendkeys):", error);
      }
    };

    automateKeyExchange();
  }, [status.isInitialized, status.keyId, userId, username]);

  /**
   * Encrypts a message for a recipient identified by username. Returns the ECDH wire string
   * plus the peer key's version, which the send stamps as e2e_keys_version.
   */
  const encrypt = async (message: string, recipientUsername: string): Promise<PeerCiphertext> => {
    if (!status.isInitialized || !status.keyId || !userId) {
      throw new Error("Encryption not initialized");
    }
    // No @keys fetch per send: the peer's key is refreshed on conversation open (ChatInputs).
    return encryptForPeer(message, recipientUsername, Number(userId));
  };

  /**
   * Checks if encryption is available for a recipient by username.
   * Stable reference (useCallback) so callers can safely include it in useEffect deps.
   */
  const canEncryptFor = useCallback(async (recipientUsername: string): Promise<boolean> => {
    const peerKeys = await getPublicKeySetByUsername(recipientUsername);
    return peerKeys !== null;
  }, []);

  /**
   * Checks if a current (not stale) symmetric key is stored for a group conversation —
   * false routes the on-open request and the send-time guard to re-request it.
   * Stable reference (useCallback) so it can be used in useEffect deps.
   */
  const canEncryptGroup = useCallback(async (groupAddress: string, refreshOwner = false): Promise<boolean> => {
    if (!userId || !username) return false;
    return hasCurrentGroupKey(groupAddress, Number(userId), username, refreshOwner);
  }, [userId, username]);

  /**
   * Encrypts a group message with the group's symmetric key, signed by the sender.
   * Returns the wire string: Base64(IV):Base64(ciphertext):Base64(signature)
   */
  const encryptForGroup = async (message: string, groupAddress: string): Promise<string> => {
    if (!status.isInitialized || !userId || !username) {
      throw new Error("Encryption not initialized");
    }
    return encryptForGroupAddress(message, groupAddress, Number(userId));
  };

  /**
   * Ensures the group has a symmetric encryption key, and distributes it to members
   * only when the key is first generated. Only the group owner should call this.
   *
   * Safe (and cheap) to call on every conversation open: once the key exists this is
   * just two IDB reads. It does NOT re-push to every member on each open — members that
   * lack the key (newly added, or peers who re-logged in and lost the old ciphertext)
   * pull it via group_key_request → respondToGroupKeyRequest, and symmetric-key drift
   * self-heals via Path B. Re-broadcasting on every open was redundant SendUserMessage
   * (+ @keys) traffic. Mirrors respondToGroupKeyRequest's `if (!existingKey)` branch.
   * Stable reference (useCallback) so callers can safely include it in useEffect deps.
   *
   * @returns true once the key is stored (the caller marks the group done), false to retry.
   */
  const setupGroupEncryption = useCallback(async (
    groupAddress: string,
    groupName: string,
    ownerUsername: string,
    members: GroupMember[]
  ): Promise<boolean> => {
    if (!status.isInitialized || !userId) return false;

    try {
      const alreadyHadKey = (await resolveGroupSymmetricKey(groupAddress)) !== null;
      await getOrGenerateGroupSymmetricKey(groupAddress, groupName, ownerUsername);
      if (!alreadyHadKey) {
        // Not a setup failure: the key is stored, and members without it request it.
        await distributeGroupKeyToMembers(groupAddress, groupName, ownerUsername, members, Number(userId))
          .catch((err) => console.error("[GroupKey] Initial key distribution failed:", err));
      }
      return true;
    } catch (err) {
      console.error("[GroupKey] setupGroupEncryption failed:", err);
      return false;
    }
  }, [status.isInitialized, userId]);

  return {
    status,
    encrypt,
    canEncryptFor,
    canEncryptGroup,
    encryptForGroup,
    setupGroupEncryption,
  };
}
